import React, { useState } from 'react';

import { useIntl } from 'react-intl';

import Button from '@atlaskit/button/new';
import { FlagsProvider } from '@atlaskit/flag';
import LinkExternalIcon from '@atlaskit/icon/core/link-external';
import InfoIcon from '@atlaskit/icon/core/status-information';
import Popup, { type TriggerProps } from '@atlaskit/popup';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box, Inline, Stack, Text, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';

// eslint-disable-next-line @atlassian/tangerine/import/no-restricted-paths
import { useFireOAuthHovered } from '../../../controllers/events/use-fire-oauth-events';
// eslint-disable-next-line @atlassian/tangerine/import/no-restricted-paths
import { useBootstrap } from '../../../controllers/store/bootstrap';
import { type NounKeys } from '../../constants/nouns';
import {
	type ProductKeys,
	ProductKeys as ProductKeysMap,
	useProductConfigs,
} from '../../constants/products';
import type { ThirdPartyConfigsBootstrap } from '../../constants/schemas/3p-config';
import type { TriggerComponentProps } from '../../types';
import type { OAuthSource } from '../../utils/events/oauth-events';
import {
	getUnifiedSearch3PConnectImprovementsCohort,
	getUnifiedSearchRightRailEnhancementsExpValue,
	isInUnifiedSearch3PConnectImprovementsTestCohort,
	serveOneClick3PConnectExperiment,
} from '../../utils/experiments';
import { getWorkspaceNameFromProduct } from '../../utils/get-workspace-name-from-product';
import type { FilterOption } from '../filters/app-filter-sidebar/show-more-apps-popup';

import { messages } from './messages';

interface OAuthHandlersProps {
	product: ProductKeys | NounKeys | undefined;
	successFlagTitle: React.ReactNode;
	failFlagTitle: React.ReactNode;
	outboundAuthUrl: string;
	onCompletedAction?: () => void;
	onDismissAction?: () => void;
	isOnSearchDialog?: boolean;
	query?: string;
	datasourceId?: string;
}

interface OAuthHandlerFunctions {
	onAuthorize: (oAuthSource: OAuthSource) => void;
	onDismiss: () => void;
}

export type UseOAuthHandlers = (props: OAuthHandlersProps) => OAuthHandlerFunctions;

export type UseIsUserOAuthed = (
	productKey: ProductKeys | NounKeys | undefined,
	datasourceId?: string | undefined,
) =>
	| {
			userNeedsOAuth: undefined;
			outboundAuthUrl: undefined;
	  }
	| {
			userNeedsOAuth: boolean;
			outboundAuthUrl: string;
	  };

type OAuthWarningWrapperProps = {
	product: ProductKeys | NounKeys | undefined;
	isSmartlink: boolean;
	isInline?: boolean;
	trigger: (props: TriggerComponentProps) => JSX.Element;
	isOnSearchDialog?: boolean;
	searchQuery?: string;
	useOAuthHandlers: UseOAuthHandlers;
	useIsUserOAuthed: UseIsUserOAuthed;
	option?: FilterOption;
	index?: number;
	postAuthAction?: () => void;
	oAuthSource: OAuthSource;
	datasourceId?: string;
	thirdPartyConfigs?: ThirdPartyConfigsBootstrap;
	skipFlagsProvider?: boolean;
	onAppFilterClick?: () => void;
};

const popupContentStyles = xcss({
	width: '20rem',
	padding: 'space.200',
	display: 'flex',
	flexDirection: 'column',
});

const buttonContainerStyles = xcss({
	display: 'flex',
	justifyContent: 'flex-end',
	flexDirection: 'row',
});
const buttonStyles = xcss({
	margin: 'space.100',
});

const infoIconStyles = xcss({
	marginLeft: 'space.050',
	cursor: 'pointer',
});
const popupTriggerStyles = xcss({
	display: 'flex',
	justifyContent: 'space-between',
	alignItems: 'center',
});
const connectButtonStyles = xcss({
	marginLeft: 'auto',
	right: 'space.200',
	display: 'flex',
	flexDirection: 'column',
});
const sidebarStyles = xcss({
	width: '100%',
});
const connectTextStyles = xcss({
	color: 'color.link',
	font: 'font.body',
	fontWeight: 'font.weight.medium',

	':hover': {
		color: 'color.link.pressed',
		textDecoration: 'underline',
	},
});

const singleSelectConnectTextStyles = xcss({
	color: 'color.link',
	font: 'font.body.small',
	fontWeight: 'font.weight.medium',

	':hover': {
		color: 'color.link.pressed',
		textDecoration: 'underline',
	},
});

export const OAuthWarningWrapperImpl = ({
	product,
	isSmartlink,
	isInline,
	trigger,
	isOnSearchDialog,
	searchQuery,
	useOAuthHandlers,
	useIsUserOAuthed,
	option,
	index,
	postAuthAction,
	oAuthSource,
	datasourceId,
	thirdPartyConfigs,
	onAppFilterClick,
}: OAuthWarningWrapperProps): React.JSX.Element => {
	const products = useProductConfigs();
	const [{ cloudId }] = useBootstrap();
	const [isPopupOpen, setIsPopupOpen] = useState(false);
	const { formatMessage } = useIntl();
	const { userNeedsOAuth, outboundAuthUrl } = useIsUserOAuthed(product, datasourceId);
	const fireOAuthHovered = useFireOAuthHovered({
		cloudId: cloudId || '',
		product: product || '',
		isOnSearchDialog,
		thirdPartyConfigs: thirdPartyConfigs || {},
	});

	// For Forge connectors, use workspaceName from thirdPartyConfigs
	const workspaceName = thirdPartyConfigs
		? getWorkspaceNameFromProduct(product, thirdPartyConfigs)
		: undefined;
	const productString =
		(product && workspaceName) || (product && products[product as ProductKeys]?.displayName) || '';
	const { onAuthorize, onDismiss } = useOAuthHandlers({
		product,
		successFlagTitle: formatMessage(messages.authorizationSuccess, {
			product: productString,
		}),
		failFlagTitle: formatMessage(messages.authorizationFail, {
			product: productString,
		}),
		outboundAuthUrl: outboundAuthUrl || '',
		onCompletedAction: () => {
			setIsPopupOpen(false);
			option?.showFilter();
			option?.onSelect();
			postAuthAction?.();
		},
		onDismissAction: () => {
			setIsPopupOpen(false);
		},
		isOnSearchDialog,
		query: searchQuery,
		datasourceId,
	});

	const connectMessage =
		product === ProductKeysMap.Figma || isSmartlink
			? messages.authorizeConnectThirdPartyProductAlternate
			: messages.authorizeConnectThirdPartyProduct;

	//This component is a functional wrapper that turns the trigger prop into a popup trigger.
	// if no warning is needed, we just render the trigger
	if (!product || !userNeedsOAuth || !outboundAuthUrl) {
		return trigger({ isDisabled: false });
	}

	const handleClickConnect = () => {
		onAppFilterClick?.();
		// Skip popup and connect immediately if either of these experiments are enabled
		// serveOneClick3PConnectExperiment was a SHIP decision so this will always return true
		// We aren't cleaning this experiment up in the code since there is a growth holdout attached to it
		if (serveOneClick3PConnectExperiment() || isInUnifiedSearch3PConnectImprovementsTestCohort()) {
			onAuthorize(oAuthSource);
			return;
		}
		setIsPopupOpen(!isPopupOpen);
	};

	const handleConnectHover = () => {
		setTimeout(() => {
			fireOAuthHovered(oAuthSource);
		}, 1000);
	};

	const connectButton = () => {
		const tooltipMessage =
			product === ProductKeysMap.Figma || isSmartlink
				? messages.appFilterTooltipContentAlternate
				: messages.appFilterTooltipContent;

		switch (getUnifiedSearch3PConnectImprovementsCohort()) {
			case 'icon-and-tooltip':
				return (
					<Tooltip testId="connect-button-tooltip" content={formatMessage(tooltipMessage)}>
						<Box
							testId="connect-button-with-context"
							xcss={connectButtonStyles}
							onMouseEnter={handleConnectHover}
						>
							<Inline space="space.050" alignBlock="center">
								<Box
									xcss={
										getUnifiedSearchRightRailEnhancementsExpValue() === 'single-select'
											? singleSelectConnectTextStyles
											: connectTextStyles
									}
								>
									{formatMessage(messages.connect)}
								</Box>
								<LinkExternalIcon
									testId="link-external-icon"
									label="link-external"
									color={token('color.icon.brand')}
									size="small"
								/>
							</Inline>
						</Box>
					</Tooltip>
				);

			case 'icon-only':
				return (
					<Box
						testId="connect-button-with-context"
						xcss={connectButtonStyles}
						onMouseEnter={handleConnectHover}
					>
						<Inline space="space.050" alignBlock="center">
							<Box
								xcss={
									getUnifiedSearchRightRailEnhancementsExpValue() === 'single-select'
										? singleSelectConnectTextStyles
										: connectTextStyles
								}
							>
								{formatMessage(messages.connect)}
							</Box>
							<LinkExternalIcon
								testId="link-external-icon"
								label="link-external"
								color={token('color.icon.brand')}
								size="small"
							/>
						</Inline>
					</Box>
				);

			case 'tooltip-only':
				return (
					<Tooltip testId="connect-button-tooltip" content={formatMessage(tooltipMessage)}>
						<Box
							testId="connect-button-with-context"
							xcss={connectButtonStyles}
							onMouseEnter={handleConnectHover}
						>
							<Box
								xcss={
									getUnifiedSearchRightRailEnhancementsExpValue() === 'single-select'
										? singleSelectConnectTextStyles
										: connectTextStyles
								}
							>
								{formatMessage(messages.connect)}
							</Box>
						</Box>
					</Tooltip>
				);

			// control
			default:
				return (
					<Box xcss={connectButtonStyles} onMouseEnter={handleConnectHover}>
						<Box
							xcss={
								getUnifiedSearchRightRailEnhancementsExpValue() === 'single-select'
									? singleSelectConnectTextStyles
									: connectTextStyles
							}
						>
							{formatMessage(messages.connect)}
						</Box>
					</Box>
				);
		}
	};

	const triggerComponent = (triggerProps: TriggerProps) => {
		const oAuthWarning = isInline ? (
			<Box xcss={infoIconStyles}>
				<InfoIcon label="oauth warning" color={token('color.icon.brand')} size="small" />
			</Box>
		) : (
			<>{connectButton()}</>
		);

		return (
			// eslint-disable-next-line @atlassian/a11y/interactive-element-not-keyboard-focusable
			<Box
				onClick={handleClickConnect}
				onMouseEnter={handleConnectHover}
				xcss={[popupTriggerStyles, !isInline && !isOnSearchDialog && sidebarStyles]}
			>
				{trigger({ oAuthWarning, isDisabled: true, triggerProps })}
			</Box>
		);
	};

	const isFurtherDownThanFourth = index && index > 3;
	const rightRailPopupPlacement = isFurtherDownThanFourth ? 'left-end' : 'left-start';

	return (
		<Popup
			isOpen={isPopupOpen}
			onClose={() => setIsPopupOpen(false)}
			placement={isOnSearchDialog || isInline ? 'bottom-start' : rightRailPopupPlacement}
			trigger={triggerComponent}
			shouldRenderToParent={true}
			content={() => (
				<Box xcss={popupContentStyles}>
					<Stack space="space.100">
						<Text>
							{formatMessage(connectMessage, {
								product: productString,
								strong: (text: React.ReactNode) => <Text as="strong">{text}</Text>,
								br: <br />,
							})}
						</Text>
					</Stack>
					<Stack xcss={buttonContainerStyles}>
						<Stack xcss={buttonStyles}>
							<Button onClick={onDismiss}>{formatMessage(messages.notNow)}</Button>
						</Stack>
						<Stack xcss={buttonStyles}>
							<Button onClick={() => onAuthorize(oAuthSource)} appearance="primary">
								{formatMessage(messages.connect)}
							</Button>
						</Stack>
					</Stack>
				</Box>
			)}
		/>
	);
};

export const OAuthWarningWrapper = (props: OAuthWarningWrapperProps): React.JSX.Element => {
	return props.isOnSearchDialog || props.skipFlagsProvider ? (
		<OAuthWarningWrapperImpl {...props} />
	) : (
		<FlagsProvider>
			<OAuthWarningWrapperImpl {...props} />
		</FlagsProvider>
	);
};
